'use client'

import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  FLOW_LABEL,
  FLOW_TO_SERVICE_TYPE,
  inferDocKind,
  useCreateOwnCase,
  useMyCaseForFlow,
} from '@/features/cases'
import { SERVICE_META } from '@/features/investor/components/home/service-meta'
import { DocumentUploadStep } from '@/features/investor/components/request/document-upload-step'
import { SlotStep } from '@/features/investor/components/request/slot-step'
import { DoneStep } from '@/features/investor/components/request/done-step'
import { InvestorShell } from '@/features/investor/components/investor-shell'

type Step = 'upload' | 'slot' | 'done'

/**
 * The simple, linear path for an existing investor requesting an additional service: upload the
 * one document that unlocks it, pick a callback time, done — back to the dashboard. Ported from
 * `nivex-design/apps/nivex-web-v3/src/pages/dashboard/RequestService.tsx`. No re-explanation of
 * the service (that's the first-time onboarding funnel's job) and no re-asking PAN (we already
 * know who they are).
 *
 * Both steps are collected locally, with no network call in between — the file and the slot are
 * submitted together in one `POST /cases/mine` (`useCreateOwnCase`), which creates the case only
 * once both are valid. This avoids the case ever existing half-formed on the backend (e.g. a
 * case with no document if the upload step alone had failed) — nothing is created until the
 * whole request succeeds. `useMyCaseForFlow` guards against re-entering this flow for a service
 * the investor already has a case for (which would otherwise 409 on submit). `flow` is
 * deliberately `'audit' | 'trades'`, not the full `FlowType` — Build's intake is a multi-step
 * wizard with no single document, so it never reaches this component (see
 * `dashboard/request/[flow]/page.tsx`, which redirects `'build'` straight to `/build-portfolio`
 * before this view is ever rendered).
 */
export function RequestServiceView({ flow }: { flow: 'audit' | 'trades' | null }) {
  const [step, setStep] = useState<Step>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [createdReference, setCreatedReference] = useState<string | null>(null)
  const [createdCaseId, setCreatedCaseId] = useState<string | null>(null)
  const createOwnCase = useCreateOwnCase()
  const existing = useMyCaseForFlow(flow ?? 'audit')

  if (!flow) {
    return (
      <InvestorShell active="home" title="Request a service">
        <Card>
          <CardContent className="text-muted-foreground py-8 text-center text-sm">
            That service isn&apos;t available to request this way.
          </CardContent>
        </Card>
      </InvestorShell>
    )
  }

  const service = SERVICE_META[flow]
  const activeTab = flow === 'audit' ? 'portfolio' : 'trades'

  if (existing.isListLoading) {
    return (
      <InvestorShell active={activeTab} title={`Request ${FLOW_LABEL[flow]}`}>
        <Skeleton className="h-40 w-full" />
      </InvestorShell>
    )
  }

  const handleUploadContinue = (picked: File) => {
    setFile(picked)
    setStep('slot')
  }

  const handleConfirmSlot = async (consultationSlotAt: string) => {
    if (!file) return
    try {
      const created = await createOwnCase.mutateAsync({
        serviceType: FLOW_TO_SERVICE_TYPE[flow],
        consultationSlotAt,
        kind: inferDocKind(flow, file),
        file,
      })
      setCreatedReference(created.reference)
      setCreatedCaseId(created.id)
      setStep('done')
    } catch {
      // Already surfaced via the mutation's own onError toast; stay on the slot step so the
      // investor can retry without re-uploading.
    }
  }

  return (
    <InvestorShell
      active={activeTab}
      title={`Request ${FLOW_LABEL[flow]}`}
      description="Upload your document, pick a time, done."
    >
      <div className="mx-auto flex min-h-[60vh] w-full max-w-lg flex-1 items-center justify-center">
        <div className="w-full">
          {existing.summary && step !== 'done' ? (
            <DoneStep
              docLabel={service.document}
              reference={existing.summary.reference}
              caseId={existing.summary.id}
            />
          ) : (
            <>
              {step === 'upload' && (
                <DocumentUploadStep docLabel={service.document} onContinue={handleUploadContinue} />
              )}
              {step === 'slot' && <SlotStep onConfirm={handleConfirmSlot} />}
              {step === 'done' && (
                <DoneStep
                  docLabel={service.document}
                  reference={createdReference ?? ''}
                  caseId={createdCaseId ?? undefined}
                />
              )}
            </>
          )}
        </div>
      </div>
    </InvestorShell>
  )
}
